
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { LogOut, User } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";

interface UserAvatarProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showDropdown?: boolean;
}

export function UserAvatar({ className, size = 'md', showDropdown = true }: UserAvatarProps) {
  const navigate = useNavigate();
  const { userProfile, signOut, isAdmin } = useAuth();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const getInitials = () => {
    if (!userProfile) return '?';
    
    const firstName = userProfile.first_name || '';
    const lastName = userProfile.last_name || '';
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (userProfile.email) {
      return userProfile.email.substring(0, 2).toUpperCase();
    }
    
    return '?';
  };

  // Define fixed pixel dimensions for each size
  const sizesMap = {
    sm: { outer: "w-8 h-8", text: "text-xs" },
    md: { outer: "w-10 h-10", text: "text-sm" },
    lg: { outer: "w-12 h-12", text: "text-base" }
  };

  const { outer: outerSizeClass, text: textSizeClass } = sizesMap[size];
  
  // Simple avatar with no dropdown
  if (!showDropdown) {
    return (
      <div className={cn("flex", isCollapsed ? "justify-center w-full" : "")}>
        <Avatar className={cn(outerSizeClass, className)}>
          <AvatarFallback className={cn("flex items-center justify-center", textSizeClass)}>
            {getInitials()}
          </AvatarFallback>
        </Avatar>
      </div>
    );
  }

  // Avatar with dropdown - desktop only
  return (
    <div className={cn("relative hidden md:block", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button 
            className="flex items-center justify-center p-0 bg-transparent hover:bg-transparent focus:outline-none"
            aria-label="User menu"
          >
            <Avatar className={outerSizeClass}>
              <AvatarFallback className={cn("flex items-center justify-center", textSizeClass)}>
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-50 mt-1 w-48">
          <DropdownMenuItem onClick={() => navigate('/profile')}>
            <User className="mr-2 h-4 w-4" />
            Profile {isAdmin && "(Admin)"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => signOut()}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
